import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { Input } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import IOSSwitch from "../../../components/atoms/custom-toggle-button/custom-toggle-button";
import { PosLayout } from "../../../components";
import { PosInnerPageLayout } from "../../../components/layouts/pos-layout/PosInnerPageLayout";
import CustomDateTimePicker from "../../../components/atoms/custom-date-time-picker/custom-date-time-picker";
import { CustomButton } from "../../../components";
import ConveyorInputConfigTable from "./ConvayorInputConfigTable";
import ConveyorInputHistoryTable from "./ConveyorInputHistoryTable";
import { Colors } from "../../../configs";
import { pxToRem } from "../../../util";
import ScanWarningModal from "./ScanWarningModal";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import {
  assignConveyorsAction,
  getAssignedConveyorsAction,
  validateAssignedConveyorAction,
} from "../../../redux/actions/authAction";
import { DatePicker } from "../../../components/atoms/custom-date-time-picker/DateRangePicker";
import { set } from "lodash";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import { getallRacksAction } from "../../../redux/actions/rackAction";
import { AssignedConveyor } from "../../../interfaces";

interface ICoveyorInputPageProps {
  openTab?: number;
}

export const CoveyorInputPage: React.FC<ICoveyorInputPageProps> = ({
  openTab = 0,
}) => {
  const dispatch = useAppDispatch();
  const {
    assignedConveyors,
    assignedConveyorsLoading,
    assignedConveyorsError,
    assignConveyorsData,
    validateAssignedConveyorError,
    validateAssignedConveyorLoading,
  } = useAppSelector((state) => state.auth);

  const SLOT_CODE_RE = /^\d{1,2}-\d{1,2}$/;
  const INVOICE_RE = /^[A-Za-z]{2}\d{2,}-\d+$/;
  const NUMERIC_RE = /^\d+$/;

  const [selectedTab, setSelectedTab] = React.useState(openTab);
  const [dueDate, setDueDate] = useState<Dayjs | null>(dayjs());
  const SLOT_PREFIX = "SLOT-";
  const STORAGE_KEY = "conveyorConfigData";
  const [submitting, setSubmitting] = useState(false);
  const [inputValues, setInputValues] = useState({
    invoice: "",
    locationSuffix: "",
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const [editingField, setEditingField] = useState<
    "invoice" | "location" | null
  >(null);
  const fullLocation = inputValues.locationSuffix.trim()
    ? `${SLOT_PREFIX}${inputValues.locationSuffix.trim()}`
    : "";
  const isAddEnabled = inputValues.invoice.trim() !== "" && fullLocation !== "";

  const [rowCount, setRowCount] = useState(0);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedRows, setSelectedRows] = useState<boolean[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState<string | undefined>();

  // Every rejected scan routes through here so the modal always states the actual
  // reason. It previously announced a duplicate invoice for all of them.
  const rejectScan = (reason: string) => {
    setWarningMessage(reason);
    setModalOpen(true);
  };
  const [rackFilter, setRackFilter] = useState<string>("All");
  const [locationFilter, setLocationFilter] = useState<string>("All");
  const [searchValue, setSearchValue] = useState<string>("");

  const [filteredConveyors, setFilteredConveyors] = useState<any[]>([]);

  const PAGE_SIZE = 20;

  const [visibleRows, setVisibleRows] = useState<AssignedConveyor[]>([]);

  const cacheRef = useRef<AssignedConveyor[]>([]);
  const nextPageRef = useRef(0);
  const prefetchingRef = useRef(false);
  const cancelTokenRef = useRef(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const { getAllRacks, getAllRacksLoading, addRacks } = useAppSelector(
    (state) => state.rack
  );
  const [rows, setRows] = React.useState<any[]>([]);

  const parseLocationSuffix = (suffix: string) => {
    const m = suffix.trim().match(/^(\d+)-(\d+)$/);
    if (!m) return null;

    return { rack: String(Number(m[1])), location: String(Number(m[2])) };
  };

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : [];

    const numbered = parsed.map((entry: any, index: number) => ({
      ...entry,
      no: index + 1,
    }));

    setRows(numbered);
    setRowCount(numbered.length);
    setSelectedRows(new Array(numbered.length).fill(selectAll));
  }, [selectAll, setRowCount, setSelectedRows]);

  useEffect(() => {
    setRowCount(rows.length);
  }, [rows.length, setRowCount]);

  const fetchConveyors = () => {
    if (!dueDate) return;

    const params = {
      conveyorId: rackFilter !== "All" ? Number(rackFilter) : undefined,
      slotId:
        rackFilter !== "All" && locationFilter !== "All"
          ? getSlotId(conveyors, rackFilter, locationFilter)
          : undefined,
      billNumber: searchValue.trim() || undefined,
      date: dueDate.format("YYYY-MM-DD"),
      page: 0,
      size: 20,
    };

    dispatch(getAssignedConveyorsAction(params));
  };

  const buildParams = (pageNum: number) => ({
    conveyorId: rackFilter !== "All" ? Number(rackFilter) : undefined,
    slotId:
      rackFilter !== "All" && locationFilter !== "All"
        ? getSlotId(conveyors, rackFilter, locationFilter)
        : undefined,
    billNumber: searchValue.trim() || undefined,
    date: dueDate?.format("YYYY-MM-DD"),
    page: pageNum,
    size: PAGE_SIZE,
  });

  const loadFirstPage = async () => {
    const token = ++cancelTokenRef.current;

    setHasMore(true);
    cacheRef.current = [];
    setVisibleRows([]);
    nextPageRef.current = 0;

    const res: any = await dispatch(getAssignedConveyorsAction(buildParams(0)));
    if (cancelTokenRef.current !== token) return;

    const first = Array.isArray(res?.payload) ? res.payload : [];
    cacheRef.current = first;
    setVisibleRows(first);
    nextPageRef.current = 1;
    setHasMore(first.length === PAGE_SIZE);

    prefetchAll(token);
  };

  const prefetchAll = async (token: number) => {
    if (prefetchingRef.current) return;
    prefetchingRef.current = true;

    try {
      for (;;) {
        const p = nextPageRef.current;
        if (!hasMore) break;

        const res: any = await dispatch(
          getAssignedConveyorsAction(buildParams(p))
        );
        if (cancelTokenRef.current !== token) return;

        const items = Array.isArray(res?.payload) ? res.payload : [];
        if (items.length === 0) {
          setHasMore(false);
          break;
        }

        cacheRef.current = cacheRef.current.concat(items);
        nextPageRef.current = p + 1;

        if (items.length < PAGE_SIZE) {
          setHasMore(false);
          break;
        }

        await new Promise((r) => setTimeout(r, 0));
      }
    } finally {
      prefetchingRef.current = false;
    }
  };

  const prefetchOne = async () => {
    const token = cancelTokenRef.current;
    const p = nextPageRef.current;

    if (!hasMore) return;

    const res: any = await dispatch(getAssignedConveyorsAction(buildParams(p)));
    if (cancelTokenRef.current !== token) return;

    const items = Array.isArray(res?.payload) ? res.payload : [];
    cacheRef.current = cacheRef.current.concat(items);
    nextPageRef.current = p + 1;
    if (items.length < PAGE_SIZE) setHasMore(false);
  };

  const revealNextChunk = async () => {
    const already = visibleRows.length;
    const cached = cacheRef.current.length;

    if (already < cached) {
      const next = cacheRef.current.slice(already, already + PAGE_SIZE);
      setVisibleRows((prev) => prev.concat(next));
      return;
    }

    if (hasMore) {
      await prefetchOne();
      const cached2 = cacheRef.current.length;
      if (cached2 > already) {
        const next = cacheRef.current.slice(already, already + PAGE_SIZE);
        setVisibleRows((prev) => prev.concat(next));
      }
    }
  };

  useEffect(() => {
    if (selectedTab === 1 && dueDate) loadFirstPage();
  }, [selectedTab, dueDate, rackFilter, locationFilter]);

  const norm = (v: unknown) => (v == null ? "" : String(v).toLowerCase());
  const filteredConveyorss = useMemo(() => {
    if (!visibleRows.length) return [];
    const q = searchValue.trim().toLowerCase();
    if (!q) return visibleRows;
    return visibleRows.filter(
      (c) =>
        norm(c.cashierId).includes(q) ||
        norm(c.billId).includes(q) ||
        norm(c.slotName).includes(q) ||
        norm(c.date).includes(q) ||
        norm(c.time).includes(q)
    );
  }, [visibleRows, searchValue]);

  useEffect(() => {
    if (selectedTab === 1) {
      fetchConveyors();
    }
  }, [dueDate, rackFilter, locationFilter, selectedTab]);

  useEffect(() => {
    if (!assignedConveyors || assignedConveyors.length === 0) {
      setFilteredConveyors([]);
      return;
    }

    let filtered = [...assignedConveyors];

    if (searchValue.trim() !== "") {
      const q = searchValue.trim().toLowerCase();
      filtered = filtered.filter(
        (c) =>
          norm(c.cashierId).includes(q) ||
          norm(c.billId).includes(q) ||
          norm(c.slotName).includes(q) ||
          norm(c.date).includes(q) ||
          norm(c.time).includes(q)
      );
    }

    setFilteredConveyors(filtered);
  }, [assignedConveyors, searchValue]);

  useEffect(() => {
    setRowCount(rows.length);
    setSelectedRows(new Array(rows.length).fill(false));
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleDateChange = (newValue: Dayjs | null) => {
    setDueDate(newValue);
  };

  const readStore = () => JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  const snackbar = useSnackbarContext();

  const billExists = (billId: string) =>
    readStore().some(
      (r: any) =>
        String(r.billId).trim().toLowerCase() === billId.trim().toLowerCase()
    );

  const slotExists = (rack: string, location: string) =>
    readStore().some(
      (r: any) => String(r.rack) === rack && String(r.location) === location
    );

  const handleRackFilterChange = (value: string) => {
    setRackFilter(value);
  };

  const handleLocationFilterChange = (value: string) => {
    setLocationFilter(value);
  };

  useEffect(() => {
    dispatch(getallRacksAction());
  }, [dispatch]);

  const conveyors = (getAllRacks?.body ?? []) as Array<{
    id: number;
    convayorName?: string;
    conveyorName?: string;
    slots?: any[];
  }>;

  const rackOptions = useMemo(
    () =>
      conveyors.map((c) => ({
        value: String(c.id),
        label: c.convayorName ?? c.conveyorName ?? `Conveyor ${c.id}`,
        slotCount: c.slots?.length ?? 0,
      })),
    [conveyors]
  );

  const selectedConveyor = useMemo(
    () => conveyors.find((c) => String(c.id) === rackFilter),
    [conveyors, rackFilter]
  );

  const slotCount = useMemo(
    () =>
      selectedConveyor?.slots?.length ??
      conveyors.reduce((m, c) => Math.max(m, c.slots?.length ?? 0), 0),
    [selectedConveyor, conveyors]
  );

  const locationOptions = useMemo(
    () => Array.from({ length: slotCount }, (_, i) => String(i + 1)),
    [slotCount]
  );

  const getSlotId = (
    conveyors: Array<{ id: number; slots?: any[] }>,
    rackId: string | number,
    slotOrdinal: string | number
  ): number | undefined => {
    const conv = conveyors.find((c) => String(c.id) === String(rackId));
    if (!conv?.slots?.length) return undefined;

    const idx = Number(slotOrdinal) - 1;
    if (!Number.isFinite(idx) || idx < 0 || idx >= conv.slots.length)
      return undefined;

    const s = conv.slots[idx];

    const real = typeof s === "object" ? s.slotId ?? s.id : s;
    return Number.isFinite(Number(real)) ? Number(real) : undefined;
  };

  useEffect(() => {
    if (slotCount <= 0) return;
    if (locationFilter === "All") return;

    const n = Number(locationFilter);
    if (Number.isFinite(n) && n >= 1 && n <= slotCount) return;

    setLocationFilter("All");
  }, [slotCount, locationFilter]);

  const handleValidateAndAdd = async () => {
    const { invoice, locationSuffix } = inputValues;

    const parts = parseLocationSuffix(locationSuffix);
    if (!parts) {
      rejectScan(
        `"${locationSuffix.trim()}" is not a valid location. Scan or type it as ` +
        `<rack>-<location>, for example 4-4.`
      );
      return;
    }

    // A repeated invoice and an occupied location are different mistakes and need
    // different corrections, so they no longer share one message.
    if (billExists(invoice)) {
      rejectScan(
        `Invoice ${invoice.trim()} is already in the list below. ` +
        `Scan a different invoice.`
      );
      return;
    }

    if (slotExists(parts.rack, parts.location)) {
      rejectScan(
        `Rack ${parts.rack}, location ${parts.location} is already taken by ` +
        `another invoice in the list below.`
      );
      return;
    }

    const slotId = getSlotId(conveyors, parts.rack, parts.location);
    if (!slotId) {
      rejectScan(
        `Rack ${parts.rack} has no location ${parts.location}. ` +
        `Check the rack label and try again.`
      );
      return;
    }
    const slotKey = `${SLOT_PREFIX}${parts.rack}-${slotId}`;

    try {
      const result = await dispatch(
        validateAssignedConveyorAction({
          billId: invoice.trim(),
          slotKey,
        })
      ).unwrap();

      // `body` is true when the bill or the slot is already taken, and also when
      // the API rejected the bill/slot itself — in both cases the row must not be
      // queued, and `message` is what tells the cashier which of the two it was.
      const isBlocked = Boolean(result.body);

      if (!isBlocked) {
        const currentData =
          JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]") || [];

        const newEntry = {
          date: dayjs().format("YYYY/MM/DD"),
          time: dayjs().format("HH:mm"),
          cashierId: 4,
          billId: invoice.trim(),
          rack: parts.rack,
          location: parts.location,
          slotKey,
        };

        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify([...currentData, newEntry])
        );

        setRefreshKey((k) => k + 1);

        setInputValues({ invoice: "", locationSuffix: "" });
        snackbar.showSnackbar("Assigned bill to table", "success");
      } else {
        // The API answers "Success" even when reporting a conflict, so that carries
        // no reason; anything else (an invalid bill or slot) does. The modal is the
        // single message here — pairing it with a snackbar produced two notices that
        // could contradict each other.
        rejectScan(
          result.message && result.message !== "Success"
            ? result.message
            : `Invoice ${invoice.trim()} or rack ${parts.rack}-${parts.location} ` +
            `is already assigned on the server.`
        );
      }
    } catch (error) {
      console.error("Validation failed", error);
      rejectScan(
        "Could not check this invoice against the server. " +
        "Check your connection and try again."
      );
    }
  };

  const handleAssignConveyors = async () => {
    if (submitting) return;
    const allRows = readStore();
    const selectedBills = allRows
      .filter((_: any, index: any) => selectedRows[index])
      .map((row: any) => ({ billId: row.billId, slotKey: row.slotKey }));

    if (selectedBills.length === 0) return;

    setSubmitting(true);
    try {
      const requestPayload = { cashierId: 4, assignments: selectedBills };
      await dispatch(assignConveyorsAction(requestPayload)).unwrap();

      snackbar.showSnackbar("Bills added to the racks successfully", "success");

      // Only the selected rows were sent — clearing the whole store here used to
      // discard the queued rows the cashier had deliberately left unticked.
      const remaining = allRows.filter(
        (_: any, index: number) => !selectedRows[index]
      );
      localStorage.setItem(STORAGE_KEY, JSON.stringify(remaining));

      setSelectedRows(new Array(remaining.length).fill(false));
      setRowCount(remaining.length);
      setSelectAll(false);
      setRefreshKey((k) => k + 1);
    } catch (err: any) {
      snackbar.showSnackbar(
        typeof err === "string" ? err : "Failed to assign bills to racks",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const localData =
      JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]") || [];
    setRowCount(localData.length);
    setSelectedRows(new Array(localData.length).fill(false));
  }, []);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const allSelected =
    rowCount > 0 &&
    selectedRows.length === rowCount &&
    selectedRows.every(Boolean);

  const addIfReady = useCallback(() => {
    setTimeout(() => {
      if (inputValues.invoice.trim() && inputValues.locationSuffix.trim()) {
        handleValidateAndAdd();
      }
    }, 0);
  }, [inputValues.invoice, inputValues.locationSuffix]);

  useEffect(() => {
    let buffer = "";
    let timer: number | null = null;

    const reset = () => {
      buffer = "";
      if (timer) window.clearTimeout(timer);
      timer = null;
    };

    const processScan = (s: string) => {
      if (!s) return;

      if (SLOT_CODE_RE.test(s)) {
        setInputValues((prev) => {
          const next = { ...prev, locationSuffix: s };
          return next;
        });
        addIfReady();
        return;
      }

      if (INVOICE_RE.test(s) || NUMERIC_RE.test(s)) {
        setInputValues((prev) => {
          const next = { ...prev, invoice: s };
          return next;
        });
        addIfReady();
        return;
      }
    };

    const shouldIgnore = () => {
      const el = document.activeElement as HTMLElement | null;
      const tag = (el?.tagName || "").toUpperCase();
      const editable =
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        el?.isContentEditable;

      return editable || !!editingField;
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (shouldIgnore()) return;

      if (e.key === "Enter") {
        processScan(buffer.trim());
        reset();
        return;
      }

      if (e.key.length === 1) {
        buffer += e.key;

        if (timer) window.clearTimeout(timer);
        timer = window.setTimeout(() => {
          processScan(buffer.trim());
          reset();
        }, 120);
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [editingField, addIfReady, handleValidateAndAdd, setInputValues]);

  return (
    <PosLayout>
      <PosInnerPageLayout title={""}>
        <Box sx={{ padding: "1rem -2rem" }}>
          <Typography
            sx={{
              fontSize: pxToRem(48),
              fontWeight: "bold",
              color: Colors.MAIN_GRAY,
              marginTop: "1rem",
              marginLeft: pxToRem(66),
              flex: 1,
            }}
          >
            Rack Config
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
              alignItems: "center",
              marginTop: "-2rem",
              marginLeft: pxToRem(70),
            }}
          >
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              TabIndicatorProps={{
                style: { display: "none" },
              }}
              sx={{
                "& .MuiTab-root": {
                  background: Colors.BACKSHADE_GRAY,
                  color: Colors.PRIMARY,
                  height: pxToRem(43),
                  minHeight: pxToRem(43),
                  width: "11.9285714286rem",
                  padding: 0,
                  marginTop: "3rem",
                  textTransform: "none",
                },
                "& .Mui-selected": {
                  background: Colors.PRIMARY,
                  color: `${Colors.WHITE} !important`,
                },
                "& .MuiTab-root:first-of-type": {
                  borderRadius: "0.71rem 0 0 0",
                },
                "& .MuiTab-root:nth-of-type(2)": {
                  borderRadius: "0 0.71rem 0 0",
                },
              }}
            >
              <Tab
                test-name="Config-tab"
                label="Config"
                sx={{ fontWeight: "bold", fontSize: "1.142rem" }}
              />
              <Tab
                test-name="History-tab"
                label="History"
                sx={{ fontWeight: "bold", fontSize: "1.142rem" }}
              />
            </Tabs>
          </Box>
        </Box>

        <Box
          sx={{
            backgroundColor: Colors.BACKSHADE_GRAY,
            padding: "2rem",
            borderRadius: "0 0.71rem 0.71rem 0.71rem",
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            justifyContent: "flex-start",
            height: pxToRem(827),
            width: pxToRem(1726),
            marginLeft: pxToRem(70),
            gap: "1rem",
          }}
        >
          {selectedTab === 0 && (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  width: "100%",
                  padding: "2rem 2rem",
                }}
              >
                <Box
                  sx={{
                    width: pxToRem(756),
                    height: pxToRem(320),
                    backgroundColor: Colors.WHITE,
                    borderRadius: pxToRem(15),
                    textAlign: "center",
                    padding: pxToRem(37),
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: pxToRem(32),
                      fontWeight: "bold",
                      color: Colors.TEXT_GRAY_DARK,
                    }}
                  >
                    Invoice
                  </Typography>

                  <Box
                    test-name="Invoice-button"
                    sx={{
                      width: pxToRem(684),
                      height: pxToRem(188),
                      backgroundColor: Colors.BACKSHADE_GRAY,
                      borderRadius: pxToRem(10),
                      marginTop: pxToRem(10),
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                    onClick={() => setEditingField("invoice")}
                  >
                    {editingField === "invoice" ? (
                      <Input
                        value={inputValues.invoice}
                        onChange={(e) =>
                          setInputValues((p) => ({
                            ...p,
                            invoice: e.target.value.slice(0, 10),
                          }))
                        }
                        autoFocus
                        disableUnderline
                        onBlur={() => setEditingField(null)}
                        inputProps={{ maxLength: 10 }}
                        sx={{
                          fontSize: pxToRem(64),
                          color: Colors.TEXT_GRAY_DARK,
                          textAlign: "center",
                          width: "100%",
                          "& .MuiInputBase-input": { textAlign: "center" },
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          fontSize: pxToRem(64),
                          color:
                            inputValues.invoice.trim() === ""
                              ? Colors.ACCENT_GRAY_DISABLED
                              : Colors.TEXT_GRAY_DARK,
                        }}
                      >
                        {inputValues.invoice.trim() === ""
                          ? "DCXX-XXXX"
                          : inputValues.invoice}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box
                  sx={{
                    width: pxToRem(756),
                    height: pxToRem(320),
                    backgroundColor: Colors.WHITE,
                    borderRadius: pxToRem(15),
                    textAlign: "center",
                    padding: pxToRem(37),
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: pxToRem(32),
                      fontWeight: "bold",
                      color: Colors.TEXT_GRAY_DARK,
                    }}
                  >
                    Location
                  </Typography>

                  <Box
                    test-name="Location-button"
                    sx={{
                      width: pxToRem(684),
                      height: pxToRem(188),
                      backgroundColor: Colors.BACKSHADE_GRAY,
                      borderRadius: pxToRem(10),
                      mt: pxToRem(10),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      editingField !== "location" && setEditingField("location")
                    }
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "baseline",
                        gap: pxToRem(8),
                        lineHeight: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: pxToRem(64),
                          color: Colors.TEXT_GRAY_DARK,
                        }}
                      >
                        {SLOT_PREFIX}
                      </Typography>

                      {editingField === "location" ? (
                        <Input
                          value={inputValues.locationSuffix}
                          onChange={(e) => {
                            const v = e.target.value
                              .replace(/[^0-9-]/g, "")
                              .slice(0, 7);
                            setInputValues((p) => ({
                              ...p,
                              locationSuffix: v,
                            }));
                          }}
                          autoFocus
                          disableUnderline
                          onBlur={() => setEditingField(null)}
                          onKeyDown={(e) =>
                            e.key === "Enter" && setEditingField(null)
                          }
                          placeholder="X-X"
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9-]*",
                            maxLength: 7,
                          }}
                          sx={{
                            fontSize: pxToRem(64),
                            color: Colors.TEXT_GRAY_DARK,
                            "& .MuiInputBase-input": {
                              padding: 0,
                              width: pxToRem(105),

                              textAlign: "left",
                            },
                          }}
                        />
                      ) : (
                        <Typography
                          sx={{
                            fontSize: pxToRem(64),
                            color:
                              inputValues.locationSuffix.trim() === ""
                                ? Colors.ACCENT_GRAY_DISABLED
                                : Colors.TEXT_GRAY_DARK,
                            display: "inline-block",
                            minWidth: `${Math.max(
                              3,
                              (inputValues.locationSuffix || "2-1").length
                            )}ch`,
                            textAlign: "left",
                          }}
                        >
                          {inputValues.locationSuffix.trim() === ""
                            ? "X-X"
                            : inputValues.locationSuffix}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "100%",
                }}
              >
                <CustomButton
                  test-name="Add-button"
                  sx={{
                    width: pxToRem(126),
                    height: pxToRem(46),
                    fontSize: pxToRem(16),
                    marginRight: pxToRem(25),
                    marginTop: pxToRem(-15),
                  }}
                  disabled={!isAddEnabled || validateAssignedConveyorLoading}
                  onClick={handleValidateAndAdd}
                >
                  {validateAssignedConveyorLoading ? (
                    <CircularProgress size={20} sx={{ color: "white" }} />
                  ) : (
                    "Add"
                  )}
                </CustomButton>
              </Box>

              <Box
                sx={{
                  width: pxToRem(1635),
                  height: pxToRem(230),
                  marginLeft: pxToRem(20),
                  marginTop: pxToRem(20),
                  borderRadius: pxToRem(10),
                  display: "flex",
                }}
              >
                <ConveyorInputConfigTable
                  key={refreshKey}
                  setRowCount={setRowCount}
                  selectAll={selectAll}
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                  refreshKey={refreshKey}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: pxToRem(10),
                    marginLeft: pxToRem(25),
                  }}
                >
                  <IOSSwitch
                    test-name="Select-all-checkbox"
                    sx={{ mt: "-2px" }}
                    disabled={rowCount === 0}
                    checked={allSelected}
                    onChange={(e) => {
                      const checked = e.target.checked;

                      setSelectAll(checked);
                      setSelectedRows(new Array(rowCount).fill(checked));
                    }}
                  />
                  <Typography
                    sx={{ fontSize: pxToRem(16), color: Colors.MAIN_GRAY }}
                  >
                    Select All
                  </Typography>
                </Box>

                <CustomButton
                  test-name="Submit-button"
                  sx={{
                    width: pxToRem(150),
                    height: pxToRem(46),
                    fontSize: pxToRem(16),
                    marginRight: pxToRem(25),
                    marginTop: pxToRem(10),
                  }}
                  disabled={submitting || !selectedRows.some(Boolean)}
                  onClick={handleAssignConveyors}
                >
                  {submitting ? (
                    <CircularProgress size={20} sx={{ color: "#fff" }} />
                  ) : (
                    "Submit"
                  )}
                </CustomButton>
              </Box>
            </>
          )}

          {selectedTab === 1 && (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  padding: "1rem 2rem",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: pxToRem(5),
                    position: "relative",
                    marginTop: pxToRem(-25),
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: pxToRem(18),
                      color: Colors.TEXT_GRAY_DARK,
                      marginTop: pxToRem(20),
                    }}
                  >
                    Date:
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <DatePicker
                      type="single"
                      value={dueDate ? dueDate.toDate() : undefined}
                      onChange={(val) => {
                        if (val instanceof Date || val === undefined) {
                          setDueDate(val ? dayjs(val) : null);
                        }
                      }}
                      compact
                      className="w-[243%] h-[3.1rem] bg-white"
                      popoverProps={{ style: { zIndex: 9999 } }}
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: pxToRem(20),
                    justifyContent: "flex-end",
                    flex: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: pxToRem(5),
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: pxToRem(18),
                        color: Colors.TEXT_GRAY_DARK,
                      }}
                    >
                      Rack:
                    </Typography>

                    <Select
                      fullWidth
                      size="small"
                      value={rackFilter}
                      onChange={(e) => handleRackFilterChange(e.target.value)}
                      sx={{
                        width: pxToRem(268),
                        height: pxToRem(42),
                        fontSize: pxToRem(16),
                        backgroundColor: Colors.WHITE,
                      }}
                      disabled={getAllRacksLoading}
                    >
                      <MenuItem value="All" sx={{ fontSize: pxToRem(16) }}>
                        All
                      </MenuItem>
                      {rackOptions.map((opt) => (
                        <MenuItem
                          key={opt.value}
                          value={opt.value}
                          sx={{ fontSize: pxToRem(16) }}
                        >
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: pxToRem(5),
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: pxToRem(18),
                        color: Colors.TEXT_GRAY_DARK,
                      }}
                    >
                      Location:
                    </Typography>
                    <Select
                      fullWidth
                      size="small"
                      value={locationFilter}
                      onChange={(e) =>
                        handleLocationFilterChange(e.target.value)
                      }
                      sx={{
                        width: pxToRem(268),
                        height: pxToRem(42),
                        fontSize: pxToRem(16),
                        backgroundColor: Colors.WHITE,
                      }}
                      disabled={
                        getAllRacksLoading ||
                        rackFilter === "All" ||
                        slotCount === 0
                      }
                    >
                      <MenuItem value="All" sx={{ fontSize: pxToRem(16) }}>
                        All
                      </MenuItem>
                      {locationOptions.map((loc) => (
                        <MenuItem
                          key={loc}
                          value={loc}
                          sx={{ fontSize: pxToRem(16) }}
                        >
                          {loc.padStart(2, "0")}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: pxToRem(10),
                    }}
                  >
                    <TextField
                      placeholder="Search"
                      value={searchValue}
                      onChange={handleSearchChange}
                      sx={{
                        width: pxToRem(268),
                        marginTop: pxToRem(30),
                        backgroundColor: Colors.WHITE,
                        "& .MuiInputBase-root": {
                          fontSize: pxToRem(16),
                          height: pxToRem(42),
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <SearchIcon
                            sx={{
                              width: pxToRem(18),
                              height: pxToRem(18),
                              color: Colors.MAIN_GRAY,
                            }}
                          />
                        ),
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginTop: pxToRem(10),
                  position: "relative",
                }}
              >
                {assignedConveyorsLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: pxToRem(200),
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : assignedConveyorsError ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: pxToRem(200),
                    }}
                  >
                    <Typography color="error">
                      Error: {assignedConveyorsError}
                    </Typography>
                  </Box>
                ) : (
                  <ConveyorInputHistoryTable
                    data={filteredConveyorss}
                    loading={assignedConveyorsLoading}
                    onLoadMore={revealNextChunk}
                    hasMore={
                      hasMore || visibleRows.length < cacheRef.current.length
                    }
                  />
                )}
              </Box>
            </>
          )}
        </Box>

        <ScanWarningModal
          open={modalOpen}
          message={warningMessage}
          onClose={() => setModalOpen(false)}
        />
      </PosInnerPageLayout>
    </PosLayout>
  );
};

export default CoveyorInputPage;
